/* eslint-disable react/prop-types */
import React from 'react';
import { VerifiedRounded } from '@mui/icons-material';
import gitmeLogo from '../../images/logo/gitme-logo.svg';
import {
  getCertificationDisplayName,
  getCertificationThemeKey,
  getCertificationVerificationTagline
} from '../../constants/certificationBadgeCopy';

/* ────────────────────────────────────────────────────────
   Helpers
   ──────────────────────────────────────────────────────── */

/* ── Mathematically perfect flat-top hexagon path ── */
const hexPath = (cx, cy, r) => {
  const pts = Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i; // 0°, 60°, 120°, 180°, 240°, 300°
    return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy - r * Math.sin(a)).toFixed(1)}`;
  });
  return `M${pts.join(' L')} Z`;
};

/* ── Smart title split for two balanced lines ── */
const splitTitle = name => {
  const words = name.split(' ');
  if (words.length === 2) {
    return words;
  }
  if (words.length <= 1) return [name, ''];
  let best = 1;
  let bestDiff = Infinity;
  for (let i = 1; i < words.length; i++) {
    const d = Math.abs(
      words.slice(0, i).join(' ').length - words.slice(i).join(' ').length
    );
    if (d < bestDiff) {
      bestDiff = d;
      best = i;
    }
  }
  return [words.slice(0, best).join(' '), words.slice(best).join(' ')];
};

/* ────────────────────────────────────────────────────────
   Badge color themes — rich, premium palettes
   ──────────────────────────────────────────────────────── */

const THEME = {
  delivers_with_impact: {
    outerFrom: '#E8B840',
    outerTo: '#8A6018',
    mid: '#FBF0D0',
    innerFrom: '#FFFCF2',
    innerTo: '#FFF8E8',
    ribbon: '#C89028',
    ribbonDark: '#8A6018',
    ribbonLight: '#F0C848',
    fold: '#604808',
    stars: '#E0B040',
    certText: '#A07820',
    separator: '#F0D890'
  },
  delivers_with_precision: {
    outerFrom: '#8B7BFF',
    outerTo: '#4338CA',
    mid: '#ECE9FF',
    innerFrom: '#FAF8FF',
    innerTo: '#F1EEFF',
    ribbon: '#5B4AE6',
    ribbonDark: '#31258F',
    ribbonLight: '#8A7CFF',
    fold: '#23195F',
    stars: '#7C6CFF',
    certText: '#5143D8',
    separator: '#CFC8FF'
  },
  built_to_last: {
    outerFrom: '#28D080',
    outerTo: '#087040',
    mid: '#D0F2E4',
    innerFrom: '#F2FCF6',
    innerTo: '#E8FAF0',
    ribbon: '#18A868',
    ribbonDark: '#086840',
    ribbonLight: '#40D890',
    fold: '#044828',
    stars: '#48D898',
    certText: '#109058',
    separator: '#98E4C0'
  }
};

const getTheme = b =>
  THEME[getCertificationThemeKey(b)] || THEME.delivers_with_impact;

/* ────────────────────────────────────────────────────────
   Pre-computed hex paths
   ──────────────────────────────────────────────────────── */

const CX = 160;
const CY = 112;
const OUTER_HEX = hexPath(CX, CY, 98);
const MID_HEX = hexPath(CX, CY, 84);
const INNER_HEX = hexPath(CX, CY, 75);

/* Star polygon string */
const STAR =
  '0,-5.8 1.6,-2 5.5,-2 2.6,0.9 3.4,4.8 0,2.8 -3.4,4.8 -2.6,0.9 -5.5,-2 -1.6,-2';

/* ────────────────────────────────────────────────────────
   HexBadgeSVG — the actual premium SVG badge
   ──────────────────────────────────────────────────────── */

const HexBadgeSVG = ({ badge, size = 200, logoSrc, crop = 'full' }) => {
  const displayName = getCertificationDisplayName(badge);
  const isLocked = badge?.status === 'not_earned';
  const theme = getTheme(badge);
  const k = getCertificationThemeKey(badge);
  const [line1, line2] = splitTitle(displayName);
  const badgeYear = String(new Date().getFullYear());
  const titleFontSize =
    badge?.badge_key === 'delivers_with_precision'
      ? 10.5
      : displayName.length > 24
        ? 14
        : 16;
  const firstLineFontSize =
    badge?.badge_key === 'delivers_with_precision' ? 12.4 : titleFontSize;
  const secondLineFontSize =
    badge?.badge_key === 'delivers_with_precision' ? 9.8 : titleFontSize;
  const viewBox = crop === 'tight' ? '38 14 244 204' : '0 0 320 230';
  const aspectRatio = crop === 'tight' ? 204 / 244 : 230 / 320;
  return (
    <svg
      viewBox={viewBox}
      xmlns="http://www.w3.org/2000/svg"
      style={{
        display: 'block',
        width: '100%',
        maxWidth: typeof size === 'number' ? `${size}px` : size,
        height: typeof size === 'number' ? `${size * aspectRatio}px` : 'auto',
        opacity: isLocked ? 0.48 : 1,
        filter: isLocked ? 'grayscale(0.75)' : 'none'
      }}
    >
      <defs>
        <pattern
          id={`bg${k}`}
          width="30"
          height="26"
          patternUnits="userSpaceOnUse"
        >
          <path
            d="M15 1 L28 8.5 L28 21.5 L15 29 L2 21.5 L2 8.5 Z"
            fill="none"
            stroke={theme.separator}
            strokeOpacity="0.18"
            strokeWidth="1"
          />
        </pattern>

        <linearGradient id={`og${k}`} x1="0.15" y1="0" x2="0.85" y2="1">
          <stop offset="0%" stopColor={theme.outerFrom} />
          <stop offset="100%" stopColor={theme.outerTo} />
        </linearGradient>

        <linearGradient id={`ig${k}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFFDF6" />
          <stop offset="100%" stopColor={theme.innerTo} />
        </linearGradient>

        <linearGradient id={`rg${k}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={theme.ribbonLight} />
          <stop offset="50%" stopColor={theme.ribbon} />
          <stop offset="100%" stopColor={theme.ribbonDark} />
        </linearGradient>

        <linearGradient id={`sh${k}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="white" stopOpacity="0.55" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>

        <filter id={`ds${k}`} x="-14%" y="-10%" width="128%" height="128%">
          <feDropShadow
            dx="0"
            dy="8"
            stdDeviation="10"
            floodColor={theme.outerTo}
            floodOpacity="0.24"
          />
        </filter>

        <clipPath id={`ic${k}`}>
          <path d={INNER_HEX} />
        </clipPath>
      </defs>

      <rect x="0" y="0" width="320" height="230" fill="url(#bg${k})" />

      <ellipse
        cx={CX}
        cy="102"
        rx="122"
        ry="94"
        fill={theme.mid}
        opacity={isLocked ? 0.32 : 0.5}
      />

      <path d={OUTER_HEX} fill={`url(#og${k})`} filter={`url(#ds${k})`} />

      <path d={MID_HEX} fill={theme.mid} />
      <path d={INNER_HEX} fill={`url(#ig${k})`} />
      <path
        d={hexPath(CX, CY, 67)}
        fill="none"
        stroke={theme.separator}
        strokeWidth="2"
        opacity="0.9"
      />

      <rect
        x="0"
        y="0"
        width="320"
        height={CY}
        fill={`url(#sh${k})`}
        clipPath={`url(#ic${k})`}
      />

      {[0, 1, 2].map(i => (
        <polygon
          key={i}
          points={STAR}
          fill={theme.stars}
          transform={`translate(${CX - 20 + i * 20}, 66) scale(1.15)`}
        />
      ))}

      <text
        x={CX}
        y="92"
        textAnchor="middle"
        fontFamily="'Inter','Segoe UI',system-ui,sans-serif"
        fontSize="9"
        fontWeight="800"
        letterSpacing="2.6"
        fill={theme.certText}
      >
        CERTIFIED
      </text>

      <line
        x1="122"
        y1="102"
        x2="198"
        y2="102"
        stroke={theme.separator}
        strokeWidth="1.2"
      />

      <text
        x={CX}
        y={line2 ? '122' : '132'}
        textAnchor="middle"
        fontFamily="'Inter','Segoe UI',system-ui,sans-serif"
        fontSize={firstLineFontSize}
        fontWeight="800"
        letterSpacing="-0.45"
        fill="#0F172A"
      >
        {line1}
      </text>

      {line2 && (
        <text
          x={CX}
          y="143"
          textAnchor="middle"
          fontFamily="'Inter','Segoe UI',system-ui,sans-serif"
          fontSize={secondLineFontSize}
          fontWeight="800"
          letterSpacing="-0.45"
          fill="#0F172A"
        >
          {line2}
        </text>
      )}

      <image
        href={logoSrc}
        x="140"
        y={line2 ? '154' : '146'}
        width="40"
        height="11"
        preserveAspectRatio="xMidYMid meet"
      />

      <rect
        x="110"
        y="186"
        width="100"
        height="24"
        rx="12"
        fill="white"
        opacity="0.88"
      />
      <text
        x={CX}
        y="203"
        textAnchor="middle"
        fontFamily="'Inter','Segoe UI',system-ui,sans-serif"
        fontSize="13"
        fontWeight="900"
        letterSpacing="2.2"
        fill={theme.ribbonDark}
      >
        {badgeYear}
      </text>
    </svg>
  );
};

/* ────────────────────────────────────────────────────────
   Main CertificationBadge component
   ──────────────────────────────────────────────────────── */

const CertificationBadge = ({ badge, companyName, variant = 'dashboard' }) => {
  const displayName = getCertificationDisplayName(badge);
  const verificationTagline = getCertificationVerificationTagline(badge);
  const isLocked = badge?.status === 'not_earned';
  const isPreview = variant === 'preview';
  const isPublic = variant === 'public';
  const isDashboard = variant === 'dashboard';
  const isEmbed = variant === 'embed';

  const badgeSize = isPreview ? 330 : isPublic ? 500 : isEmbed ? 470 : 300;

  const badgeVisual = (
    <div
      className={`flex justify-center ${isPublic ? 'w-full scale-[0.86] px-1 sm:scale-[0.92]' : 'w-full'} ${
        isDashboard ? 'scale-[1.2] sm:scale-[1.24]' : ''
      } ${isEmbed ? 'scale-[1.08] sm:scale-[1.14]' : ''}`}
      style={{ transformOrigin: 'center' }}
    >
      <HexBadgeSVG
        badge={badge}
        size={badgeSize}
        logoSrc={gitmeLogo}
        crop={isPreview ? 'full' : 'tight'}
      />
    </div>
  );

  /* ─── LinkedIn share preview (1200×630) ─── */
  if (isPreview) {
    const theme = getTheme(badge);
    return (
      <div className="relative mx-auto w-[860px] overflow-hidden rounded-2xl bg-white">
        <span className="sr-only">{displayName}</span>
        {/* Gradient background */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg,
              ${theme.innerTo} 0%,
              #ffffff 30%,
              #f8fafc 70%,
              ${theme.mid}88 100%)`
          }}
        />
        {/* Subtle dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.018]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, ${theme.outerTo} 1px, transparent 1px)`,
            backgroundSize: '22px 22px'
          }}
        />

        <div className="relative grid w-[860px] grid-cols-[470px_1fr] items-stretch">
          {/* Left: Badge */}
          <div className="flex items-center justify-center px-4 py-6">
            <div style={{ transform: 'scale(1.5)', transformOrigin: 'center' }}>
              {badgeVisual}
            </div>
          </div>

          {/* Right: Content */}
          <div className="flex flex-col justify-center border-l border-slate-100/60 px-8 py-6">
            <div
              className="mb-6 inline-flex w-fit items-center gap-2 rounded-full px-4 py-2"
              style={{ background: theme.mid }}
            >
              <VerifiedRounded sx={{ fontSize: 15, color: theme.ribbon }} />
              <span
                className="text-[10px] font-black uppercase tracking-[0.16em]"
                style={{ color: theme.ribbonDark }}
              >
                GitMe Certified Achievement
              </span>
            </div>

            <h2 className="text-[48px] font-black leading-[0.96] tracking-[-0.04em] text-slate-950">
              {companyName}
            </h2>
            <p
              className="mt-3 text-[21px] font-bold leading-snug tracking-tight"
              style={{ color: theme.ribbonDark }}
            >
              {displayName}
            </p>

            <div
              className="my-7 h-[3px] w-20 rounded-full"
              style={{
                background: `linear-gradient(90deg, ${theme.ribbon}, transparent)`
              }}
            />

            <p className="mb-3 text-[14px] font-medium text-slate-500">
              {verificationTagline}
            </p>
            <div
              className="inline-flex w-fit items-center justify-center rounded-2xl border-2 bg-white px-4 py-2.5 shadow-md"
              style={{ borderColor: theme.ribbon }}
            >
              <img src={gitmeLogo} alt="GitMe" className="h-7 w-auto" />
            </div>
          </div>
        </div>

        {/* Outer border */}
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{ border: `1.5px solid ${theme.mid}` }}
        />
      </div>
    );
  }

  /* ─── Dashboard / public variant ─── */
  return (
    <div className="space-y-3">
      <span className="sr-only">{displayName}</span>
      <div className="relative py-1">
        {badgeVisual}
        {!isLocked && (
          <div
            className="absolute -bottom-2 left-1/2 h-8 w-[58%] -translate-x-1/2 rounded-full blur-2xl"
            style={{
              background: `radial-gradient(circle, ${getTheme(badge).outerTo}30 0%, transparent 72%)`
            }}
          />
        )}
      </div>

      <div className="h-1" />
    </div>
  );
};

export default CertificationBadge;
